/**
 * Chat API Service
 * Client for messaging/chat endpoints
 */

import { Conversation, Message, GetMessagesParams } from '../types/chat';
import { BASE_URL } from '../../../shared/lib/api';

export const chatApi = {
  /**
   * Get all conversations for the current user
   */
  getConversations: async (
    token: string,
    limit = 50,
    offset = 0
  ): Promise<Conversation[]> => {
    const res = await fetch(
      `${BASE_URL}/chat/conversations?limit=${limit}&offset=${offset}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!res.ok) {
      throw new Error('Failed to fetch conversations');
    }

    const data = await res.json();
    return data.conversations;
  },

  /**
   * Create or get existing 1:1 conversation with a friend
   */
  createConversation: async (
    token: string,
    friendUserId: string
  ): Promise<Conversation> => {
    const res = await fetch(`${BASE_URL}/chat/conversations`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ friendUserId }),
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to create conversation');
    }

    return res.json();
  },

  /**
   * Get messages in a conversation (paginated)
   */
  getMessages: async (
    token: string,
    conversationId: string,
    params: GetMessagesParams = {}
  ): Promise<{ messages: Message[]; hasMore: boolean }> => {
    const query = new URLSearchParams();
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.before) query.append('before', params.before);

    const res = await fetch(
      `${BASE_URL}/chat/conversations/${conversationId}/messages?${query}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!res.ok) {
      throw new Error('Failed to fetch messages');
    }

    return res.json();
  },

  /**
   * Send a message in a conversation
   */
  sendMessage: async (
    token: string,
    conversationId: string,
    body: string
  ): Promise<Message> => {
    try {
      const res = await fetch(
        `${BASE_URL}/chat/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ body }),
        }
      );

      if (!res.ok) {
        try {
          const error = await res.json();
          throw new Error(error.error || 'Failed to send message');
        } catch (parseError) {
          // If response body can't be parsed, use status text or default message
          throw new Error(res.statusText || 'Failed to send message');
        }
      }

      return res.json();
    } catch (err) {
      // Handle network errors (fetch failures, connection issues, etc.)
      if (err instanceof TypeError) {
        // Network errors: failed to fetch, connection refused, etc.
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('fetch') || 
            errorMessage.includes('network') || 
            errorMessage.includes('disconnected') ||
            errorMessage.includes('failed')) {
          throw new Error('Network error: Unable to connect to server. Please check your connection.');
        }
      }
      // Re-throw if it's already an Error with a message
      if (err instanceof Error) {
        throw err;
      }
      // Fallback for unknown errors
      throw new Error('Failed to send message');
    }
  },

  /**
   * Mark all messages in a conversation as read
   */
  markAsRead: async (token: string, conversationId: string): Promise<void> => {
    const res = await fetch(
      `${BASE_URL}/chat/conversations/${conversationId}/read`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!res.ok) {
      throw new Error('Failed to mark as read');
    }
  },

  /**
   * Get total unread message count across all conversations
   */
  getUnreadCount: async (token: string): Promise<number> => {
    const res = await fetch(`${BASE_URL}/chat/unread`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      throw new Error('Failed to fetch unread count');
    }

    const data = await res.json();
    return data.count;
  },
};

